import { useState } from 'react';

// Public landing page (bearing360.co) — the non-logged-in front door. Invite-only
// positioning: one sentence, one action (request access). Existing users sign in via the
// nav link. Faithful build of design/sprint-2/Bearing360 Landing.dc.html (Bold direction).
//
// The phone preview here IS an intentional device mockup (marketing), distinct from the app
// screens, which are built as plain mobile web. "Request an invite" is a client-only capture
// for now — there is no waitlist endpoint yet (not built without instruction, per CLAUDE.md).
export default function Landing({ onSignIn }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  function submit(e) {
    e.preventDefault();
    const ok = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
    if (!ok) return;
    // No backend yet — graceful client-side confirmation. Wiring to a real waitlist store is
    // future work (needs an endpoint, which is out of scope without instruction).
    console.log('[landing] access request (not persisted):', email.trim());
    setSent(true);
  }

  return (
    <div style={S.page}>
      <style>{CSS}</style>

      {/* Atmospheric washes */}
      <div style={S.blobTop} className="lp-breathe" />
      <div style={S.blobBottom} />

      {/* Grain */}
      <svg style={S.grain} className="lp-grain" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
        <defs>
          <filter id="lpGrain" x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence type="fractalNoise" baseFrequency="0.68" numOctaves="4" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
        </defs>
        <rect width="120%" height="120%" x="-10%" y="-10%" filter="url(#lpGrain)" />
      </svg>

      {/* Nav */}
      <nav style={S.nav} className="lp-nav lp-fade" data-d="1">
        <div style={S.wordmark}>Bearing360</div>
        <button type="button" onClick={onSignIn} style={S.signIn} className="lp-signin">Sign in</button>
      </nav>

      {/* Hero */}
      <div style={S.hero} className="lp-hero">
        <div style={S.eyebrow} className="lp-fade" data-d="2">Private beta · invite only</div>

        <h1 style={S.h1} className="lp-fade" data-d="3">
          Your life has a direction.<br />
          <em style={S.h1em}>Bearing360</em> keeps it in view.
        </h1>

        <p style={S.subhead} className="lp-fade" data-d="4">
          A calm daily brief for the things you are carrying.<br />
          An evening reflection to help you close the day.<br />
          Context you do not have to repeat.
        </p>

        <div style={S.ctaGroup} className="lp-fade" data-d="5">
          {sent ? (
            <div style={S.sentMsg}>Thank you. We'll reach out when it's right.</div>
          ) : (
            <>
              <form style={S.ctaPill} onSubmit={submit}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  style={S.input}
                  className="lp-input"
                  aria-label="Email address"
                />
                <button type="submit" style={S.ctaBtn} className="lp-cta">Request an invite</button>
              </form>
              <div style={S.ctaNote}>
                We are opening slowly with a small group.<br />
                If Bearing seems like a fit, we will write to you.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Phone preview */}
      <div style={S.previewWrap} className="lp-fade" data-d="6">
        <PhonePreview />
      </div>

      {/* Footer */}
      <div style={S.footer} className="lp-fade" data-d="7">
        <div style={S.copy}>© 2026 Bearing360</div>
        <div style={{ display: 'flex', gap: 28 }}>
          <span style={S.footLink}>Privacy</span>
          <span style={S.footLink}>Terms</span>
        </div>
      </div>
    </div>
  );
}

// Fictional demo content only — no real companies, clients, or names.
const BRIEF_ITEMS = [
  { name: 'Client transition', desc: 'Final handover needs review before Wednesday.' },
  { name: 'Health', desc: 'Follow-up needs to move around Thursday travel.' },
  { name: 'Independent project', desc: 'Two possible directions remain open.' },
];

// Condensed Briefing mockup inside a device shell — marketing visual only.
// Uses fictional content; no real names, projects, or locations.
function PhonePreview() {
  return (
    <div style={S.phoneShell} className="lp-phone-shell">
      <div style={S.phoneScreen}>
        <div style={S.island} />

        {/* Dusk gradient band with greeting */}
        <div style={S.ppBand}>
          <div style={S.ppGrad} />
          <div style={S.ppBandFade} />
          <div style={S.ppStatus}><span style={S.ppTime}>9:41</span></div>
          <div style={S.ppGreetWrap}>
            <div style={S.ppGreet}>Good morning.</div>
          </div>
        </div>

        {/* Brief summary */}
        <div style={S.ppBrief}>
          Three things need your attention today. One has a deadline. Two need a decision.
        </div>
        <div style={S.ppDivider} />

        {/* Workstream items */}
        <div style={S.ppItems}>
          {BRIEF_ITEMS.map((item, i) => (
            <div
              key={item.name}
              style={{ ...S.ppItem, borderBottom: i < BRIEF_ITEMS.length - 1 ? '1px solid #EAE6E1' : 'none' }}
            >
              <div style={S.ppItemName}>{item.name}</div>
              <div style={S.ppItemDesc}>{item.desc}</div>
            </div>
          ))}
        </div>

        {/* Carried forward — memory / standing context */}
        <div style={S.ppCarryDivider} />
        <div style={S.ppCarry}>
          <div style={S.ppCarryLabel}>Carried forward</div>
          <div style={S.ppCarryText}>
            You chose to keep the launch narrow until the handover is closed.
          </div>
        </div>
      </div>
    </div>
  );
}

const GRAD = 'linear-gradient(170deg, #5A7894 0%, #84A0B6 20%, #AAA08E 42%, #C6B898 60%, #D8CAAE 80%, #E4D8C2 100%)';

const CSS = `
@keyframes lpFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes lpBreathe { 0%,100% { opacity: 0.30; transform: scale(1); } 50% { opacity: 0.65; transform: scale(1.012); } }
@keyframes lpDrift { 0% { transform: translate(0,0);} 25% { transform: translate(-1%,1%);} 50% { transform: translate(1%,-1%);} 75% { transform: translate(-0.5%,0.5%);} 100% { transform: translate(0,0);} }
.lp-fade { opacity: 0; animation: lpFade 0.7s both; }
.lp-fade[data-d="1"] { animation-delay: 0.10s; animation-duration: 0.6s; }
.lp-fade[data-d="2"] { animation-delay: 0.20s; animation-duration: 0.6s; }
.lp-fade[data-d="3"] { animation-delay: 0.30s; }
.lp-fade[data-d="4"] { animation-delay: 0.42s; }
.lp-fade[data-d="5"] { animation-delay: 0.56s; animation-duration: 0.6s; }
.lp-fade[data-d="6"] { animation-delay: 0.70s; animation-duration: 0.9s; }
.lp-fade[data-d="7"] { animation-delay: 0.80s; animation-duration: 0.6s; }
.lp-breathe { animation: lpBreathe 8s ease-in-out infinite; }
.lp-grain { animation: lpDrift 8s ease-in-out infinite; }
.lp-input::placeholder { color: #B0ACA7; }
.lp-cta:hover { background: #517094; }
.lp-signin:hover { color: #46668c; }
/* Responsive phone shell — scales down on narrow viewports so it is never cut off */
.lp-phone-shell { width: min(300px, calc(100vw - 40px)); aspect-ratio: 300 / 648; height: auto !important; }
/* Grain is subtle on desktop but compounds with muted text on mobile — tone it down */
@media (max-width: 640px) { .lp-grain { opacity: 0.018 !important; } }
@media (prefers-reduced-motion: reduce) {
  .lp-fade, .lp-breathe, .lp-grain { animation: none !important; opacity: 1 !important; }
}
`;

const S = {
  page: { minHeight: '100vh', background: '#F0EDE8', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden', fontFamily: "'Inter', system-ui, sans-serif" },
  blobTop: { position: 'fixed', top: -160, right: -160, width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(90,120,148,0.22) 0%, rgba(198,184,152,0.14) 45%, transparent 72%)', pointerEvents: 'none', zIndex: 0 },
  blobBottom: { position: 'fixed', bottom: -200, left: -100, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(198,184,152,0.16) 0%, transparent 68%)', pointerEvents: 'none', zIndex: 0 },
  grain: { position: 'fixed', inset: 0, width: '100%', height: '100%', opacity: 0.055, pointerEvents: 'none', zIndex: 1 },

  nav: { position: 'relative', zIndex: 10, padding: '32px clamp(20px,6vw,48px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  wordmark: { fontFamily: "'Crimson Pro', serif", fontSize: 18, fontWeight: 400, color: '#2A2825', letterSpacing: '0.04em', textTransform: 'uppercase' },
  signIn: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#5B7FA6', fontWeight: 500, letterSpacing: '0.01em', padding: 0 },

  // Hero: slightly tighter vertical padding to leave room for the larger phone preview
  hero: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'clamp(32px,6vw,64px) clamp(20px,6vw,48px) 36px', position: 'relative', zIndex: 10, textAlign: 'center' },
  eyebrow: { fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#A8A49F', fontWeight: 400, marginBottom: 24 },
  h1: { fontFamily: "'Crimson Pro', serif", fontSize: 'clamp(40px,6vw,76px)', fontWeight: 300, color: '#2A2825', lineHeight: 1.12, margin: '0 0 24px', maxWidth: 760, letterSpacing: '-0.01em', textWrap: 'balance' },
  h1em: { fontStyle: 'italic', fontWeight: 300, color: '#5B7FA6' },
  // Three-line supporting copy; slightly smaller to keep hero compact across screen heights
  subhead: { fontFamily: "'Crimson Pro', serif", fontSize: 'clamp(17px,2.1vw,21px)', fontWeight: 300, color: '#7A7670', lineHeight: 1.75, margin: '0 0 44px', maxWidth: 480, textWrap: 'pretty' },

  ctaGroup: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, width: '100%' },
  ctaPill: { display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #E0DDD8', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)', width: 'min(420px, 90vw)' },
  input: { flex: 1, border: 'none', outline: 'none', background: 'transparent', fontFamily: "'Inter', sans-serif", fontSize: 14, color: '#2A2825', padding: '14px 18px', minWidth: 0 },
  ctaBtn: { background: '#5B7FA6', border: 'none', color: '#fff', fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500, padding: '12px 22px', cursor: 'pointer', whiteSpace: 'nowrap', margin: 4, borderRadius: 8, letterSpacing: '0.01em' },
  ctaNote: { fontFamily: "'Inter', sans-serif", fontSize: 11.5, color: '#B0ACA7', lineHeight: 1.65, maxWidth: 'min(420px,90vw)', textAlign: 'center' },
  sentMsg: { fontFamily: "'Crimson Pro', serif", fontSize: 19, fontStyle: 'italic', color: '#5B7FA6', lineHeight: 1.5, padding: '8px 0' },

  // Closer to the CTA than before (no top padding) — the phone feels connected to the form
  previewWrap: { display: 'flex', justifyContent: 'center', padding: '0 clamp(20px,6vw,48px) 72px', position: 'relative', zIndex: 10 },

  // Phone shell: 300 × 648 base dimensions (25% larger than original 240 × 519).
  // The CSS class .lp-phone-shell overrides these to be responsive (min() + aspect-ratio).
  phoneShell: { width: 300, height: 648, background: '#17171A', borderRadius: 44, padding: 7, boxShadow: '0 56px 80px rgba(0,0,0,0.28), 0 20px 40px rgba(0,0,0,0.16), 0 8px 16px rgba(0,0,0,0.10), inset 0 0 0 1px rgba(255,255,255,0.06)', flex: 'none', position: 'relative' },
  phoneScreen: { width: '100%', height: '100%', background: '#F0EDE8', borderRadius: 38, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' },
  island: { position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', width: 90, height: 26, background: '#17171A', borderRadius: 14, zIndex: 20 },

  // Gradient hero band — unchanged height, same dusk gradient
  ppBand: { height: 164, flex: 'none', position: 'relative', overflow: 'hidden' },
  ppGrad: { position: 'absolute', inset: 0, background: GRAD },
  ppBandFade: { position: 'absolute', bottom: 0, left: 0, right: 0, height: 60, background: 'linear-gradient(to bottom, transparent, #F0EDE8)' },
  ppStatus: { height: 36, position: 'relative', zIndex: 10, display: 'flex', alignItems: 'flex-end', padding: '0 18px 5px' },
  ppTime: { fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.92)', flex: 1, letterSpacing: '-0.02em' },
  ppGreetWrap: { position: 'absolute', bottom: 32, left: 18, right: 18, zIndex: 5 },
  ppGreet: { fontFamily: "'Crimson Pro', serif", fontSize: 22, fontWeight: 400, color: '#fff', lineHeight: 1.15, textShadow: '0 2px 14px rgba(0,0,0,0.14)' },

  // Brief intro — calm sentence, not a task list
  ppBrief: { padding: '16px 18px 14px', flex: 'none', fontFamily: "'Crimson Pro', serif", fontSize: 13, fontWeight: 400, color: '#2A2825', lineHeight: 1.65, textWrap: 'pretty' },
  ppDivider: { margin: '0 18px 0', height: 1, background: '#E0DDD8', flex: 'none' },

  // Briefing items: name + short description line; no checkboxes, counts, or dots
  ppItems: { padding: '0 18px', flex: 'none', display: 'flex', flexDirection: 'column' },
  ppItem: { padding: '10px 0', display: 'flex', flexDirection: 'column', gap: 4 },
  ppItemName: { fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 500, color: '#2A2825', letterSpacing: '-0.01em' },
  ppItemDesc: { fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#7A7670', lineHeight: 1.5 },

  // "Carried forward" section — memory / standing context, visually distinct from items
  ppCarryDivider: { margin: '4px 0 0', height: 1, background: '#E0DDD8', flex: 'none' },
  ppCarry: { padding: '12px 18px 16px', background: '#F5F2EE', flex: 1 },
  ppCarryLabel: { fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#A8A49F', marginBottom: 6 },
  ppCarryText: { fontFamily: "'Crimson Pro', serif", fontSize: 12, fontStyle: 'italic', color: '#7A7670', lineHeight: 1.55 },

  footer: { position: 'relative', zIndex: 10, padding: '24px clamp(20px,6vw,48px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #E8E4DE' },
  copy: { fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#C4C0BB', letterSpacing: '0.04em' },
  footLink: { fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#C4C0BB', letterSpacing: '0.02em' },
};
